import { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { DownloadButton } from "./DownloadAction";
import { getFileDownloadUrl } from "../../utils/deliveryApi";
import { getDeliveryPreviewKind } from "../../utils/deliveryFileTypes";
import { usePreviewNavigation } from "../../hooks/usePreviewNavigation";
import { usePreviewViewportZoom } from "../../hooks/usePreviewViewportZoom";
import { useTranslation } from "../../i18n";

const MAX_TEXT_PREVIEW_BYTES = 512 * 1024;

function DeliveryTextPreviewBody({ file, url }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [content, setContent] = useState("");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        setContent("");

        fetch(url)
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const buffer = await response.arrayBuffer();
                if (buffer.byteLength > MAX_TEXT_PREVIEW_BYTES) {
                    throw new Error(
                        t("delivery.actions.textTooLarge", {
                            size: Math.round(MAX_TEXT_PREVIEW_BYTES / 1024),
                        })
                    );
                }
                return new TextDecoder("utf-8", { fatal: false }).decode(buffer);
            })
            .then((text) => {
                if (!cancelled) {
                    setContent(text);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message || t("delivery.actions.textLoadFailed"));
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [url, t]);

    if (loading) {
        return (
            <div className="delivery-file-preview__loading">
                <Spin />
                <p>{t("delivery.actions.loadingText")}</p>
            </div>
        );
    }

    if (error) {
        return <p className="delivery-file-preview__error">{error}</p>;
    }

    return (
        <pre className="delivery-file-preview__text" aria-label={file.name}>
            {content}
        </pre>
    );
}

function DeliveryFilePreview({ file, open, onClose, onDownload, files = [], onNavigate }) {
    const { t } = useTranslation();
    const kind = file ? getDeliveryPreviewKind(file) : null;
    const url = file ? getFileDownloadUrl(file) : null;
    usePreviewViewportZoom(open);

    // 仅对图片启用左右切换，避免与 PDF/视频/文本预览的滚动、播放冲突。
    const navEnabled = kind === "image" && Boolean(onNavigate) && files.length > 1;
    const currentIndex = useMemo(
        () => (file && navEnabled ? files.findIndex((entry) => entry.id === file.id) : -1),
        [file, files, navEnabled]
    );
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < files.length - 1;

    const goPrev = useCallback(() => {
        if (hasPrev) {
            onNavigate?.(files[currentIndex - 1]);
        }
    }, [hasPrev, onNavigate, files, currentIndex]);

    const goNext = useCallback(() => {
        if (hasNext) {
            onNavigate?.(files[currentIndex + 1]);
        }
    }, [hasNext, onNavigate, files, currentIndex]);

    const { swipeHandlers } = usePreviewNavigation({
        open,
        enabled: navEnabled,
        onPrev: goPrev,
        onNext: goNext,
    });

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        },
        [onClose]
    );

    const handleDownloadClick = useCallback(() => {
        onDownload?.(file);
    }, [file, onDownload]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        document.addEventListener("keydown", handleKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [handleKeyDown, open]);

    if (!open || !file) {
        return null;
    }

    const renderBody = () => {
        if (!url) {
            return <p className="delivery-file-preview__error">{t("delivery.actions.previewUrlFailed")}</p>;
        }

        switch (kind) {
            case "image":
                return (
                    <div
                        className="delivery-file-preview__image-wrap"
                        aria-label={file.name}
                    >
                        {navEnabled && hasPrev && (
                            <button
                                type="button"
                                className="delivery-file-preview__nav delivery-file-preview__nav--prev"
                                onClick={goPrev}
                                aria-label={t("delivery.actions.previous")}
                            >
                                <LeftOutlined />
                            </button>
                        )}
                        {navEnabled && hasNext && (
                            <button
                                type="button"
                                className="delivery-file-preview__nav delivery-file-preview__nav--next"
                                onClick={goNext}
                                aria-label={t("delivery.actions.next")}
                            >
                                <RightOutlined />
                            </button>
                        )}
                        <img
                            key={file.id}
                            src={url}
                            alt=""
                            draggable={false}
                            className="delivery-file-preview__image"
                            {...swipeHandlers}
                        />
                    </div>
                );
            case "pdf":
                return (
                    <iframe
                        title={file.name}
                        src={url}
                        className="delivery-file-preview__pdf"
                    />
                );
            case "text":
                return (
                    <div className="delivery-file-preview__text-wrap">
                        <DeliveryTextPreviewBody file={file} url={url} />
                    </div>
                );
            case "video":
                return (
                    <div className="delivery-file-preview__video-wrap">
                        <video
                            src={url}
                            controls
                            playsInline
                            className="delivery-file-preview__video"
                            aria-label={file.name}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    const closeLabel = t("delivery.actions.close");

    return (
        <div
            className={`delivery-file-preview delivery-file-preview--${kind || "unknown"}`}
            role="dialog"
            aria-modal="true"
            aria-label={file.name}
        >
            <button
                type="button"
                className="delivery-file-preview__backdrop"
                onClick={onClose}
                aria-label={closeLabel}
            />
            <div className="delivery-file-preview__panel">
                <button
                    type="button"
                    className="delivery-file-preview__close"
                    onClick={onClose}
                    aria-label={closeLabel}
                >
                    ×
                </button>
                {renderBody()}
                <p className="delivery-file-preview__name">{file.name}</p>
                <div className="delivery-file-preview__actions">
                    <DownloadButton onClick={handleDownloadClick} />
                </div>
            </div>
        </div>
    );
}

export default DeliveryFilePreview;
